// SuperWbActivityPage — /super/wb-activity.
//
// Cross-tenant viewer over `wb_activity` (migration 142): a per-event audit of
// weighbridge ticket lifecycle transitions with the richer event-specific
// `metadata` payload. Backed by GET /super/wb-activity.
//
// All filters live in the URL query string so the view is bookmarkable and
// shareable. The endpoint REQUIRES a numeric company_id, so the table stays in
// a "pick a company" empty state (and the query stays disabled) until one is
// chosen from the dropdown — the numeric id comes from the /super/companies
// list (which now exposes `id` alongside `enc_id`).

import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Badge, type BadgeVariant } from '../../components/ui/Badge/Badge';
import {
  useAllCompanies,
  useWbActivity,
  WB_ACTIVITY_EVENT_TYPES,
  type ListWbActivityFilters,
  type WbActivityEntry,
  type WbActivityEventType,
} from './hooks/useSuper';
import styles from './WbActivityPage.module.css';

const PER_PAGE = 50;

// Event-type → readable badge. Unknown events fall back to the raw string with
// a neutral variant so a future event type still renders sensibly.
const EVENT_META: Record<string, { label: string; variant: BadgeVariant }> = {
  'ticket.created': { label: 'Created', variant: 'success' },
  'ticket.completed': { label: 'Completed', variant: 'info' },
  'ticket.deleted': { label: 'Deleted', variant: 'danger' },
  'ticket.weight_captured': { label: 'Weight captured', variant: 'muted' },
  'ticket.snapshot_added': { label: 'Snapshot added', variant: 'muted' },
  'ticket.patched': { label: 'Updated', variant: 'muted' },
  'ticket.voided': { label: 'Voided', variant: 'warning' },
  'ticket.reissued': { label: 'Reissued', variant: 'info' },
  'ticket.advice_note_sent': { label: 'Advice note sent', variant: 'muted' },
};

function eventMeta(eventType: string): { label: string; variant: BadgeVariant } {
  return EVENT_META[eventType] ?? { label: eventType, variant: 'default' };
}

// `DD MMM HH:mm` in the viewer's local time. Full ISO goes in the cell title.
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${hh}:${mm}`;
}

function formatIsoFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString();
}

// Entity ref column: ticket number when present, else the enc_id truncated to
// 8 chars (it's the only handle on a row without a number), else a dash.
function entityRefLabel(entry: WbActivityEntry): string {
  if (entry.entity_ref !== null && entry.entity_ref.length > 0) {
    return entry.entity_ref;
  }
  if (entry.entity_enc_id !== null && entry.entity_enc_id.length > 0) {
    return `${entry.entity_enc_id.slice(0, 8)}…`;
  }
  return '—';
}

// MariaDB returns the JSON `metadata` column as a string; tolerate both a raw
// string and an already-parsed object. Returns a flat object for the key-value
// renderer, or null when there's nothing object-shaped to show.
function parseMetadata(meta: unknown): Record<string, unknown> | null {
  let value: unknown = meta;
  if (typeof value === 'string') {
    if (value.length === 0) return null;
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function renderMetaValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function SuperWbActivityPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const companiesQuery = useAllCompanies({ limit: 500 });
  const companies = companiesQuery.data?.companies ?? [];

  // Filters are read straight off the URL so the page is bookmarkable.
  const companyIdRaw = searchParams.get('company_id') ?? '';
  const companyId =
    companyIdRaw.length > 0 && Number.isFinite(Number(companyIdRaw))
      ? Number(companyIdRaw)
      : undefined;
  const eventType = (searchParams.get('event_type') ?? '') as
    | WbActivityEventType
    | '';
  const fromDate = searchParams.get('from_date') ?? '';
  const toDate = searchParams.get('to_date') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  // Mutate one query param. Any change other than paging resets to page 1 so
  // the user never lands on an out-of-range page after narrowing the filters.
  function setParam(key: string, value: string): void {
    setExpandedId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value.length === 0) next.delete(key);
        else next.set(key, value);
        if (key !== 'page') next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  const filters: ListWbActivityFilters = useMemo(() => {
    const out: ListWbActivityFilters = { page, per_page: PER_PAGE };
    if (companyId !== undefined) out.company_id = companyId;
    if (eventType !== '') out.event_type = eventType;
    if (fromDate.length > 0) out.from_date = fromDate;
    if (toDate.length > 0) out.to_date = toDate;
    return out;
  }, [companyId, eventType, fromDate, toDate, page]);

  const query = useWbActivity(filters, { enabled: companyId !== undefined });
  const data = query.data;
  const rows: WbActivityEntry[] = data?.activity ?? [];
  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / PER_PAGE);

  return (
    <div className={styles.page}>
      <PageHeader
        title="WB Activity Log"
        subtitle="Cross-tenant per-event audit of weighbridge ticket activity."
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/super')}>
            Back
          </Button>
        }
      />

      <Card flush>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <label htmlFor="wba-company">Company</label>
            <select
              id="wba-company"
              className={styles.filterInput}
              value={companyIdRaw}
              onChange={(e) => setParam('company_id', e.target.value)}
            >
              <option value="">Select a company…</option>
              {companies.map((c) => (
                <option key={c.enc_id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="wba-event">Event type</label>
            <select
              id="wba-event"
              className={styles.filterInput}
              value={eventType}
              onChange={(e) => setParam('event_type', e.target.value)}
            >
              <option value="">All</option>
              {WB_ACTIVITY_EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {eventMeta(t).label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="wba-from">From date</label>
            <input
              id="wba-from"
              type="date"
              className={styles.filterInput}
              value={fromDate}
              onChange={(e) => setParam('from_date', e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="wba-to">To date</label>
            <input
              id="wba-to"
              type="date"
              className={styles.filterInput}
              value={toDate}
              onChange={(e) => setParam('to_date', e.target.value)}
            />
          </div>
        </div>

        {query.error ? (
          <div className={styles.errorBanner} role="alert">
            Failed to load activity: {query.error.message}
          </div>
        ) : null}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Company</th>
                <th>Event</th>
                <th>Entity ref</th>
                <th>Actor</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {companyId === undefined ? (
                <tr>
                  <td colSpan={6}>
                    <div className={styles.emptyState}>
                      Select a company to view its weighbridge activity.
                    </div>
                  </td>
                </tr>
              ) : query.isLoading ? (
                <tr>
                  <td colSpan={6}>
                    <div className={styles.empty}>
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className={styles.emptyState}>
                      No activity recorded for the selected filters.
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((entry) => {
                  const isOpen = expandedId === entry.id;
                  const meta = eventMeta(entry.event_type);
                  const parsed = parseMetadata(entry.metadata);
                  return (
                    <Fragment key={entry.id}>
                      <tr
                        className={styles.row}
                        onClick={() => setExpandedId(isOpen ? null : entry.id)}
                      >
                        <td
                          className={styles.timestamp}
                          title={formatIsoFull(entry.created_at)}
                        >
                          {formatTime(entry.created_at)}
                        </td>
                        <td>
                          {entry.company_name ?? (
                            <span className={styles.muted}>{entry.company_id}</span>
                          )}
                        </td>
                        <td>
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                        </td>
                        <td className={styles.timestamp}>{entityRefLabel(entry)}</td>
                        <td>
                          {entry.actor_name ?? (
                            <span className={styles.muted}>System</span>
                          )}
                        </td>
                        <td className={styles.detailsToggle}>
                          {isOpen ? 'Hide' : 'View'}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className={styles.expandedRow}>
                          <td
                            colSpan={6}
                            data-testid={`wb-activity-expanded-${entry.id}`}
                          >
                            {parsed === null ? (
                              <div className={styles.emptyMeta}>
                                No metadata recorded.
                              </div>
                            ) : (
                              <dl className={styles.metaList}>
                                {Object.entries(parsed).map(([key, value]) => (
                                  <div className={styles.metaRow} key={key}>
                                    <dt className={styles.metaKey}>{key}</dt>
                                    <dd className={styles.metaValue}>
                                      {renderMetaValue(value)}
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {companyId !== undefined ? (
          <div className={styles.paginationWrap}>
            <div className={styles.perPageRow}>
              <span>
                {total === 0
                  ? 'Showing 0 entries'
                  : `Showing ${(page - 1) * PER_PAGE + 1}–${Math.min(
                      page * PER_PAGE,
                      total,
                    )} of ${total} entries`}
              </span>
            </div>
            <div className={styles.perPageRow}>
              <Button
                variant="secondary"
                onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                Previous
              </Button>
              <span>
                Page {page} of {Math.max(1, totalPages)}
              </span>
              <Button
                variant="secondary"
                onClick={() => setParam('page', String(page + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
